import { NetworkClient } from "./net/NetworkClient.js";
import { installDiagnostics } from "./diagnostics.js";
import { t } from "./i18n/index.js";
import { IntroVideo } from "./ui/IntroVideo.js";
import { JoinPanel } from "./ui/JoinPanel.js";
import { LoadingScreen } from "./ui/LoadingScreen.js";
import { SettingsPanel } from "./ui/SettingsPanel.js";
import { el } from "./ui/dom.js";
import "./style.css";

/**
 * Boot order matters on a ₹10k phone over 4G.
 *
 * The opening film runs first, over a join screen that is already built and
 * waiting behind it. Phaser — by far the biggest thing we ship — is loaded
 * only once the fan shows a sign of life, so the engine download and its parse
 * overlap with the parts of the flow that are not waiting on us: watching the
 * film, and typing a name.
 */

const uiRoot = document.getElementById("ui-root");
if (!uiRoot) throw new Error("#ui-root is missing from the page");

installDiagnostics();

if (!hasWebGL()) {
  showUnsupported(uiRoot);
} else {
  void boot(uiRoot);
}

async function boot(root: HTMLElement): Promise<void> {
  const net = new NetworkClient();
  const loading = new LoadingScreen(root);
  const settings = new SettingsPanel(root);
  settings.setVisible(true);

  // Stood up when the film is over rather than now: parsing Phaser is about a
  // second of main-thread work and would hitch the one thing on screen.
  let engine: EngineLoader | null = null;
  const loadEngine = (): EngineLoader => (engine ??= engineLoader(net, loading));

  new JoinPanel(root, async ({ name, avatarId }) => {
    // Curtain back up: this is the one wait the fan actually asked for.
    loading.show();
    try {
      // Submitting is itself engagement. Assistive tech and voice control can
      // activate a button without ever firing pointerdown, and a join that hangs
      // waiting for a gesture that never comes is the worst possible failure.
      const loader = loadEngine();
      loader.start();
      await loader.ready;
      await net.join(name, avatarId);
    } catch (err) {
      // Put the card back, or the failure message renders behind the curtain.
      loading.hide();
      throw err;
    }
    loading.finish();
  });

  // The join card is plain DOM and is ready the moment it is appended. Get the
  // curtain out of its way immediately: it is opaque and full-screen, so
  // leaving it up hides the very form whose typing time we are trying to
  // overlap the engine parse with.
  loading.set("ui");
  loading.hide();

  // The film covers everything, so the join card and the curtain behind it are
  // built and settled before its first frame.
  new IntroVideo(root, (played) => {
    const loader = loadEngine();
    // A film that actually ran and then ended — watched through or skipped — is
    // the sign of life the engine has been waiting for, and it hands us the
    // whole join form's worth of typing time to parse in. A film the browser
    // refused to start proves nothing, so that case waits as it always did.
    if (played) loader.start();
  });

  // Leave cleanly on tab close so the room does not hold a ghost for 10s.
  window.addEventListener("pagehide", () => net.leave());
}

interface EngineLoader {
  /** Begin loading now. Safe to call repeatedly. */
  start(): void;
  /** Resolves once the venue can be walked around. */
  ready: Promise<void>;
}

/**
 * Load the engine on the first sign the fan is actually here: a tap, a
 * keystroke, focusing the name field — or submitting the form.
 *
 * There is deliberately no timer fallback. A page nobody is using should not
 * spend a second of CPU parsing an engine nobody asked for, and the bytes are
 * already arriving either way (see the preload tag in vite.config.ts), so the
 * parse is all that waits.
 */
function engineLoader(net: NetworkClient, loading: LoadingScreen): EngineLoader {
  const events = ["pointerdown", "keydown", "touchstart", "focusin"] as const;
  let start!: () => void;

  const ready = new Promise<void>((resolve) => {
    let started = false;
    start = () => {
      if (started) return;
      started = true;
      for (const name of events) window.removeEventListener(name, start);
      resolve(startEngine(net, loading));
    };
  });

  for (const name of events) {
    window.addEventListener(name, start, { passive: true });
  }

  return { start, ready };
}

/** Load Phaser and stand the venue up. Resolves once it can be walked around. */
async function startEngine(net: NetworkClient, loading: LoadingScreen): Promise<void> {
  loading.set("engine");
  const [{ default: Phaser }, { VenueScene }, { HudScene }] = await Promise.all([
    import("phaser"),
    import("./scenes/VenueScene.js"),
    import("./scenes/HudScene.js"),
  ]);

  loading.set("venue");
/** Where a chosen renderer is remembered, so it survives the reload. */
const RENDERER_KEY = "zg.renderer";

/**
 * GPUs whose WebGL cannot draw this venue.
 *
 * A Mali-G68 MC4 renders the floor, the walls and the untextured joystick as
 * long parallel diagonal streaks while sprites and text come out perfect.
 * Restricting the texture batch to one unit changed nothing, and untextured
 * geometry smearing rules the texture path out anyway -- it is vertex handling
 * in the driver. The same scene on the 2D canvas draws correctly, which is
 * what confirmed it.
 *
 * Deliberately a NARROW list, and it must stay one. Mali is most of Android,
 * and a Mali-G925 in this same venue renders in WebGL perfectly -- so matching
 * `/mali/` would take a correct, fast venue away from an enormous number of
 * people to rescue a few. Only chips seen to fail belong here; everybody else
 * keeps WebGL and can switch by hand if they ever need to.
 */
const BROKEN_WEBGL = /Mali-G68/i;

/** Read the GPU without disturbing anything; "" when it cannot be had. */
function gpuName(): string {
  try {
    const probe = document.createElement("canvas");
    const gl = (probe.getContext("webgl") ??
      probe.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return "";
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    return String(
      info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
    );
  } catch {
    return "";
  }
}

/**
 * Which renderer to stand the venue up on.
 *
 * `?renderer=canvas|webgl|auto` wins and is REMEMBERED, so the link can be
 * handed to somebody whose venue is broken and their next visit is fixed too --
 * a fan should not have to keep a query string to be able to attend.
 */
function rendererType(engine: typeof Phaser): number {
  const param = new URLSearchParams(window.location.search).get("renderer");
  if (param === "canvas" || param === "webgl" || param === "auto") {
    try {
      window.localStorage.setItem(RENDERER_KEY, param);
    } catch {
      /* a preference is a convenience, not a requirement */
    }
  }

  let choice = param;
  if (!choice) {
    try {
      choice = window.localStorage.getItem(RENDERER_KEY);
    } catch {
      choice = null;
    }
  }
  if (!choice && BROKEN_WEBGL.test(gpuName())) choice = "canvas";

  if (choice === "canvas") return engine.CANVAS;
  if (choice === "webgl") return engine.WEBGL;
  return engine.AUTO;
}

/**
 * How many texture units Phaser may batch across.
 *
 * Phaser's default is to use every unit the driver reports, and its batch
 * shader then indexes them by a per-vertex attribute. Several ARM Mali
 * drivers miscompile that indexing, and the result is not a subtle artefact:
 * geometry comes out smeared into long parallel diagonal streaks while
 * sprites and text, which happen to land in the first unit, render perfectly.
 * That split is the signature, and it is what a Mali-G68 on WebGL1 produced
 * here.
 *
 * One unit takes that shader path out of use entirely. The cost is more draw
 * calls, because the batch must flush whenever the texture changes; on a
 * venue this size that is a handful per frame and no visible difference on a
 * device that was already fine.
 *
 * Applied only where it is needed. `?tex=N` overrides it either way, so the
 * theory can be tested on the affected handset without a redeploy -- `?tex=0`
 * restores Phaser's own choice.
 */
function batchTextureUnits(): number | undefined {
  const override = new URLSearchParams(window.location.search).get("tex");
  if (override !== null) {
    const wanted = Number(override);
    return Number.isFinite(wanted) && wanted > 0 ? wanted : undefined;
  }

  try {
    const probe = document.createElement("canvas");
    const gl = (probe.getContext("webgl") ??
      probe.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return undefined;
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer = String(
      info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
    );
    // Matches "ANGLE (ARM, Mali-G68 MC4, OpenGL ES 3.2)" and the bare form.
    return /mali/i.test(renderer) ? 1 : undefined;
  } catch {
    // A probe that throws tells us nothing; leave Phaser to its own default.
    return undefined;
  }
}

  const game = new Phaser.Game({
    /*
     * `?renderer=canvas` drops to the 2D canvas, `?renderer=webgl` forces
     * WebGL, and by default Phaser chooses as it always did.
     *
     * This exists to settle one question on a handset I cannot reproduce:
     * a Mali-G68 draws the venue as long diagonal streaks -- the floor, the
     * walls, and the joystick, which has no texture at all -- while sprites
     * and text come out perfect. Untextured geometry smearing rules out the
     * texture path and points at vertex handling in the driver itself, and
     * the only way to test that from here is to take the driver out of the
     * picture and see whether the same scene draws correctly.
     *
     * Canvas is slower and loses some blending, so it is not a default. It is
     * a question being asked of one phone.
     */
    type: rendererType(Phaser),
    parent: "game-root",
    backgroundColor: "#0b1026",
    scale: {
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      width: window.innerWidth,
      height: window.innerHeight,
    },
    /*
     * `roundPixels` snaps drawing to whole pixels at the RENDERER, which is
     * the half of this that could not be fixed sprite by sprite.
     *
     * Individual sprites were already rounded, and it was not enough: the
     * camera follows the player at a fractional scroll, so the whole world
     * shifts by a fraction of a pixel every frame underneath sprites that are
     * themselves on whole ones. With `pixelArt` sampling nearest, that shows
     * up as every edge in the venue crawling, and it is worst on a
     * low-resolution screen where one world pixel covers fewest device
     * pixels. Rounding here covers the camera as well as everything drawn
     * through it.
     */
    render: {
      pixelArt: true,
      antialias: false,
      roundPixels: true,
      maxTextures: batchTextureUnits(),
    },
    scene: [VenueScene, HudScene],
    callbacks: {
      preBoot: (instance) => {
        instance.scene.start("Venue", { net });
      },
    },
  });

  await new Promise<void>((resolve) => {
    game.events.once("ready", () => resolve());
    // Never leave someone staring at a bar because one event did not fire.
    window.setTimeout(resolve, 8000);
  });

  // Deliberately no hide() here. The engine usually finishes while the fan is
  // still typing, and dropping the curtain is the join flow's call to make —
  // otherwise this resolving mid-join would uncover the card again.
}

/**
 * Phaser needs WebGL; canvas fallback on a 2,000-person venue is not viable.
 *
 * The context this opens is HANDED BACK before returning, and that is the
 * whole point. A browser allows only a handful of live WebGL contexts — on
 * Android Chrome it is a very small number — and one opened here and left to
 * the garbage collector still occupies a slot. Phaser then asks for its own,
 * is refused, and quietly falls back to the Canvas 2D renderer: no blend
 * modes, no masks, so glows render as slabs and a spritesheet draws whole
 * instead of one frame. The venue comes up looking wrong rather than broken,
 * which is far harder to recognise as a fault.
 */
function hasWebGL(): boolean {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl2") ??
      canvas.getContext("webgl") ??
      canvas.getContext("experimental-webgl");
    if (!gl) return false;
    // Give the slot back immediately, so the only context that outlives this
    // check is the one the venue actually draws with.
    (gl as WebGLRenderingContext).getExtension("WEBGL_lose_context")?.loseContext();
    canvas.width = 0;
    canvas.height = 0;
    return true;
  } catch {
    return false;
  }
}

function showUnsupported(root: HTMLElement): void {
  root.appendChild(
    el("div", {
      className: "zg-overlay",
      children: [
        el("div", {
          className: "zg-card",
          children: [
            el("h1", { className: "zg-title", text: t("unsupported.title") }),
            el("p", { className: "zg-sub", text: t("unsupported.body") }),
          ],
        }),
      ],
    }),
  );
}
